import { Link } from "react-router-dom";
import ContactForm from "@/pages/about/components/ContactForm";
import { usePageMeta } from "@/hooks/usePageMeta";
import { categories } from "@/mocks/categories";
import { sites } from "@/mocks/sites";

const steps = [
  {
    icon: "ri-search-line",
    title: "搜索或浏览",
    desc: "在顶部搜索框输入网站名称或关键词，或通过左侧分类进入对应频道。",
  },
  {
    icon: "ri-layout-grid-line",
    title: "筛选与排序",
    desc: "在分类页与搜索页按访问量、更新时间或字母顺序排序，快速定位心仪网站。",
  },
  {
    icon: "ri-external-link-line",
    title: "一键直达",
    desc: "点击「访问」按钮即可在新标签页打开目标网站，没有中间跳转页。",
  },
];

const stats = [
  { value: `${sites.length}+`, label: "已收录网站" },
  { value: `${categories.length}`, label: "热门分类" },
  { value: "每日", label: "持续更新" },
];

export default function About() {
  usePageMeta({
    title: "关于我们 - 网址导航",
    description:
      "网址导航是实用型网址导航平台，通过分类与精选方式提供丰富且最新的热门网站入口，帮助用户节省搜索时间、提高访问效率。",
    keywords: "网址导航,关于网址导航,网站入口,热门网站,实用网站,网络导航",
  });

  return (
    <div>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
          <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="font-medium text-foreground-800">关于</span>
          </nav>
          <h1 className="mt-4 text-3xl font-extrabold text-foreground-950 md:text-4xl">关于网址导航</h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            网址导航是一个现代门户风格的实用型网址导航平台，围绕「快速访问热门网站的入口」这一主题，
            通过分类与精选方式为用户提供丰富且最新的热门网站入口。
          </p>

          <div className="mt-8 grid grid-cols-3 gap-4 md:max-w-2xl">
            {stats.map((s) => (
              <div key={s.label} className="rounded-lg border border-background-200 bg-background-50 p-4 text-center">
                <p className="text-2xl font-extrabold text-primary-600">{s.value}</p>
                <p className="mt-1 text-xs text-foreground-600">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
        <div className="grid gap-8 lg:grid-cols-2">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950">
              <i className="ri-flag-line text-primary-500"></i>
              网站定位
            </h2>
            <p className="mt-4 text-sm leading-7 text-foreground-700">
              网址导航面向互联网普通用户、职场办公人群、学生及各类需要快速访问常用网站的人士。
              我们把散落在网络中的热门与实用站点整理归类，让查找、访问常用网站这件事变得简单高效。
            </p>
            <p className="mt-3 text-sm leading-7 text-foreground-700">
              我们坚持「精选 + 分类」的方式：一方面持续收录最新、最有价值的网站，另一方面按资讯、视频、
              购物、教育、工具等十大门类清晰组织，帮助你在海量信息中快速定位，节省搜索时间、提高访问效率。
            </p>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950">
              <i className="ri-heart-3-line text-accent-500"></i>
              服务宗旨
            </h2>
            <ul className="mt-4 space-y-3 text-sm leading-7 text-foreground-700">
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill mt-0.5 text-accent-500"></i>
                快速：清晰的分类与搜索，几秒内找到目标网站入口。
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill mt-0.5 text-accent-500"></i>
                清晰：信息层次分明，网站名称、简介与访问按钮一目了然。
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill mt-0.5 text-accent-500"></i>
                多样：覆盖十大热门分类，持续发现新鲜实用的资源。
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill mt-0.5 text-accent-500"></i>
                直达：所有入口均直接跳转至第三方网站，不设中间页。
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12">
          <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950 md:text-2xl">
            <i className="ri-guide-line text-primary-500"></i>
            使用说明
          </h2>
          <p className="mt-2 text-sm text-foreground-600">三步即可高效使用网址导航：</p>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {steps.map((step, index) => (
              <div key={step.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <div className="flex items-center justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className={`${step.icon} text-xl`}></i>
                  </span>
                  <span className="text-3xl font-extrabold text-background-300">0{index + 1}</span>
                </div>
                <h3 className="mt-4 text-base font-semibold text-foreground-950">{step.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 grid gap-8 lg:grid-cols-2">
          <div>
            <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950 md:text-2xl">
              <i className="ri-customer-service-2-line text-primary-500"></i>
              联系我们
            </h2>
            <p className="mt-3 text-sm leading-7 text-foreground-700">
              如果你有想要推荐收录的网站、发现链接失效，或对本站有任何建议，欢迎通过下方方式与我们联系。
            </p>
            <div className="mt-5 space-y-3">
              <a
                href="mailto:hello@webnav.example.com"
                className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 hover:bg-primary-50/50"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className="ri-mail-line text-xl"></i>
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-950">电子邮箱</p>
                  <p className="text-sm text-foreground-600">hello@webnav.example.com</p>
                </div>
              </a>
              <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                  <i className="ri-time-line text-xl"></i>
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-950">反馈时间</p>
                  <p className="text-sm text-foreground-600">工作日 9:00 - 18:00，我们会尽快回复</p>
                </div>
              </div>
            </div>
            <p className="mt-4 rounded-lg bg-secondary-100 p-4 text-xs leading-relaxed text-secondary-800">
              <i className="ri-information-line mr-1"></i>
              说明：本站为网址导航演示站点，分享功能为前端模拟，收藏等需要登录的功能暂不提供。
            </p>
          </div>

          <ContactForm />
        </div>
      </section>
    </div>
  );
}